import { FC, useEffect, useState, useRef } from 'react';

import { pdf } from '@react-pdf/renderer';
import { Button, Col, Flex, Form, Input, Row, Spin, Modal } from 'antd';
import html2canvas from 'html2canvas';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import { apiV1 } from '@/api/instances';
import CustomGraphicTooltip from '@/components/RGraphicTooltip';
import { AnimatedText } from '@/components/RTextAnimation';
import KeyMetricsTable from '@/pages/Result/Summary/KeyMetricsTable';
import { SimulationResult, SimulationResultDetails } from '@/types/simulations';
import { handleError } from '@/utils';

import SimulationReportDocument from './SimulationReport';

interface ChartData {
  name: string;
  beforeStorage: number;
  afterStorage: number;
  storage: number;
}

interface CreateReportProps {
  selectedSimulations: SimulationResult[];
  selectedMetric: string;
  selectedTimeFrame: string;
  reportName: string;
  onReportNameChange: (value: string) => void;
  reportTitle: string;
  onReportTitleChange: (value: string) => void;
}

const CreateReport: FC<CreateReportProps> = ({
  selectedSimulations,
  selectedMetric,
  selectedTimeFrame,
  reportName,
  onReportNameChange,
  reportTitle,
  onReportTitleChange,
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const metricsTableRef = useRef<HTMLDivElement>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [bestSimulation, setBestSimulation] = useState<SimulationResult | null>(
    null
  );
  const [closedDataKeys, setClosedDataKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewModalVisible, setIsPreviewModalVisible] =
    useState<boolean>(false);

  const toggleClosedDataKeys = (key: string = '') => {
    if (closedDataKeys.includes(key)) {
      setClosedDataKeys(closedDataKeys.filter((item) => item !== key));
    } else {
      setClosedDataKeys([...closedDataKeys, key]);
    }
  };

  const getMetricKey = (
    metric: string,
    timeFrame: string
  ): keyof SimulationResultDetails => {
    if (metric === 'irr') {
      switch (timeFrame) {
        case 'beforeStorage':
          return 'irrValueWos';
        case 'afterStorage':
          return 'irrValue';
        case 'storage':
          return 'irrBattery';
        default:
          return 'irrValue';
      }
    } else if (metric === 'ebitda') {
      switch (timeFrame) {
        case 'beforeStorage':
          return 'ebitdaMarginWos';
        case 'afterStorage':
          return 'ebitdaMargin';
        case 'storage':
          return 'ebitdaMarginStorage';
        default:
          return 'ebitdaMargin';
      }
    }
    return 'irrValue';
  };

  const { data: simulationDetails } = useSWR<SimulationResult[]>(
    selectedSimulations.length > 0
      ? selectedSimulations.map((sim) => `/simulation/${sim.id}/`)
      : null,
    async (urls: string[]) => {
      try {
        setIsLoading(true);
        const results = await Promise.all(
          urls.map(async (url) => {
            try {
              const response = await apiV1.get(url);
              if (!response) return null;
              return response;
            } catch (error) {
              handleError(error);
              return null;
            }
          })
        );

        return results.filter(
          (result): result is SimulationResult =>
            result !== null &&
            'id' in result &&
            'result' in result &&
            typeof result.result === 'object' &&
            result.result !== null
        );
      } catch (error) {
        console.error('Failed to fetch simulation details');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
      errorRetryCount: 3,
    }
  );

  useEffect(() => {
    if (simulationDetails?.length) {
      const metricKey = getMetricKey(selectedMetric, selectedTimeFrame);
      const best = simulationDetails.reduce(
        (prev: SimulationResult, current: SimulationResult) => {
          if (!prev?.result || !current?.result) return prev || current;

          const prevValue = Number(prev.result[metricKey]) || 0;
          const currentValue = Number(current.result[metricKey]) || 0;

          return currentValue > prevValue ? current : prev;
        }
      );

      setBestSimulation(best);
    }
  }, [simulationDetails, selectedMetric, selectedTimeFrame]);

  useEffect(() => {
    if (simulationDetails?.length) {
      const data = simulationDetails
        .map((sim) => {
          if (!sim?.result) return null;

          const beforeStorageKey = getMetricKey(
            selectedMetric,
            'beforeStorage'
          );
          const afterStorageKey = getMetricKey(selectedMetric, 'afterStorage');
          const storageKey = getMetricKey(selectedMetric, 'storage');

          return {
            name: sim.name || 'Unnamed Simulation',
            beforeStorage: Number(sim.result[beforeStorageKey]).toFixed(2) || 0,
            afterStorage: Number(sim.result[afterStorageKey]).toFixed(2) || 0,
            storage: Number(sim.result[storageKey]).toFixed(2) || 0,
          };
        })
        .filter((item): item is ChartData => item !== null);

      setChartData(data);
    }
  }, [simulationDetails, selectedMetric, selectedTimeFrame]);

  const createPdfDocument = async (
    bestSimulation: SimulationResult,
    reportName: string,
    reportTitle: string,
    selectedMetric: string,
    selectedTimeFrame: string,
    chartRef: React.RefObject<HTMLDivElement>,
    getMetricKey: (
      metric: string,
      timeFrame: string
    ) => keyof SimulationResultDetails,
    metricsTableRef: React.RefObject<HTMLDivElement>
  ) => {
    if (!bestSimulation?.result) return null;

    try {
      let chartImageData = '';
      if (chartRef.current) {
        const canvas = await html2canvas(chartRef.current, {
          useCORS: true,
          scale: 4,
          backgroundColor: theme.colors.light,
        });
        chartImageData = canvas.toDataURL('image/png');
      }

      let tableImageData = '';
      if (metricsTableRef.current) {
        const tableCanvas = await html2canvas(metricsTableRef.current, {
          useCORS: true,
          scale: 4,
          backgroundColor: theme.colors.light,
        });
        tableImageData = tableCanvas.toDataURL('image/png');
      }

      const blob = await pdf(
        <SimulationReportDocument
          reportName={reportName}
          reportTitle={reportTitle}
          bestSimulation={bestSimulation}
          selectedMetric={selectedMetric}
          selectedTimeFrame={selectedTimeFrame}
          chartImageData={chartImageData}
          tableImageData={tableImageData}
          theme={theme}
          getMetricKey={getMetricKey}
        />
      ).toBlob();

      return blob;
    } catch (error) {
      console.error('PDF creation error:', error);
      return null;
    }
  };

  const previewPDF = async () => {
    if (!bestSimulation) return;

    try {
      setIsGeneratingReport(true);

      const pdfBlob = await createPdfDocument(
        bestSimulation,
        reportName,
        reportTitle,
        selectedMetric,
        selectedTimeFrame,
        chartRef,
        getMetricKey,
        metricsTableRef
      );

      if (pdfBlob) {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        const url = URL.createObjectURL(pdfBlob);
        setPreviewUrl(url);
        setIsPreviewModalVisible(true);
      }
    } catch (error) {
      console.error('PDF preview error:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const generatePDF = async () => {
    if (!bestSimulation) return;

    try {
      setIsGeneratingReport(true);

      const pdfBlob = await createPdfDocument(
        bestSimulation,
        reportName,
        reportTitle,
        selectedMetric,
        selectedTimeFrame,
        chartRef,
        getMetricKey,
        metricsTableRef
      );

      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${reportName}.pdf`;
        link.click();

        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <Form layout="vertical">
      {isGeneratingReport && (
        <div className="generating-report-container">
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: 'white', fontSize: '18px' }}>
            Preparing report, please wait...
          </div>
        </div>
      )}

      <Form.Item
        label="Report Name"
        required
        tooltip="Please enter a name for the report"
      >
        <Flex gap={16} align="center">
          <Input
            value={reportName}
            onChange={(e) => onReportNameChange(e.target.value)}
            placeholder="Enter report name"
          />
          <Input
            value={reportTitle}
            onChange={(e) => onReportTitleChange(e.target.value)}
            placeholder="Enter report title (optional)"
          />
          <Button
            onClick={previewPDF}
            disabled={
              !reportName.trim() ||
              !bestSimulation ||
              isLoading ||
              isGeneratingReport
            }
            loading={isGeneratingReport}
          >
            Preview Report
          </Button>
          <Button
            type="primary"
            onClick={generatePDF}
            disabled={
              !reportName.trim() ||
              !bestSimulation ||
              isLoading ||
              isGeneratingReport
            }
            loading={isGeneratingReport}
          >
            Generate Report
          </Button>
        </Flex>
      </Form.Item>

      {isLoading ? (
        <Flex
          justify="center"
          align="center"
          style={{
            height: '400px',
          }}
        >
          <AnimatedText text="Loading and calculating data..." />
        </Flex>
      ) : (
        <>
          <Row id="simulation-chart" ref={chartRef}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid
                  vertical={true}
                  horizontal={true}
                  strokeDasharray={'3 3'}
                />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    if (value.length > 15) {
                      return value.substring(0, 15) + '...';
                    }
                    return value;
                  }}
                  label={{
                    value: 'Simulation Names',
                    position: 'bottom',
                    offset: -15,
                    style: { fontSize: 14 },
                  }}
                />
                <YAxis
                  label={{
                    value: `${selectedMetric.toUpperCase()} Values (%)`,
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 14 },
                  }}
                />
                <Tooltip content={<CustomGraphicTooltip />} />
                <Legend
                  payload={[
                    {
                      value: 'Before Storage',
                      id: 'beforeStorage',
                      type: 'circle',
                      color: theme.colors.greenSecondary,
                    },
                    {
                      value: 'After Storage',
                      id: 'afterStorage',
                      type: 'circle',
                      color: theme.colors.blueLightAccent,
                    },
                    {
                      value: 'Storage',
                      id: 'storage',
                      type: 'circle',
                      color: theme.colors.orangeSecondary,
                    },
                  ]}
                  iconSize={10}
                  onClick={({ id }) => toggleClosedDataKeys(id)}
                />
                {!closedDataKeys.includes('beforeStorage') && (
                  <Bar
                    dataKey="beforeStorage"
                    fill={theme.colors.greenSecondary}
                    name={`${selectedMetric.toUpperCase()} - Before Storage`}
                    unit={'%'}
                  />
                )}
                {!closedDataKeys.includes('afterStorage') && (
                  <Bar
                    dataKey="afterStorage"
                    fill={theme.colors.blueLightAccent}
                    name={`${selectedMetric.toUpperCase()} - After Storage`}
                    unit={'%'}
                  />
                )}
                {!closedDataKeys.includes('storage') && (
                  <Bar
                    dataKey="storage"
                    fill={theme.colors.orangeSecondary}
                    name={`${selectedMetric.toUpperCase()} - Storage`}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </Row>
        </>
      )}

      {/* Preview Modal */}
      <Modal
        title="PDF Report Preview"
        open={isPreviewModalVisible}
        onCancel={() => setIsPreviewModalVisible(false)}
        width="80%"
        style={{ top: 20 }}
        footer={[
          <Button key="back" onClick={() => setIsPreviewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={() => {
              if (previewUrl) {
                const link = document.createElement('a');
                link.href = previewUrl;
                link.download = `${reportName}.pdf`;
                link.click();
              }
            }}
          >
            Download
          </Button>,
        ]}
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            style={{ width: '100%', height: '80vh', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>

      {/* Hidden div for PDF generation - positioned off-screen but still rendered */}
      <div className="hidden-container">
        <Row ref={metricsTableRef}>
          {bestSimulation && (
            <>
              <Col span={24} className="metrics-table">
                <KeyMetricsTable data={bestSimulation} activeTab={0} />
              </Col>
              <Col span={24} className="metrics-table">
                <KeyMetricsTable data={bestSimulation} activeTab={1} />
              </Col>
              <Col span={24} className="metrics-table mb-24">
                <KeyMetricsTable data={bestSimulation} activeTab={2} />
              </Col>
            </>
          )}
        </Row>
      </div>
    </Form>
  );
};

export default CreateReport;
